/**
 * Keeps the delivery threshold bar, the delivery top-up suggestions, and the
 * subtotal shown inside the checkout button ("checkout・£15") in step with
 * AJAX basket changes.
 *
 * WHY THIS EXISTS
 * The theme does not re-render the basket after a change. It fetches
 * /cart?section_id=api-cart-items and patches a fixed list of nodes into the
 * drawer: the line items, the upsell items, the bundle items and the price
 * holder. Anything else in the drawer keeps whatever the last full page load
 * gave it. So the bar sat frozen: adding a second £25 soap, or removing a line
 * entirely, left it showing the old figure until the page was reloaded.
 *
 * WHY IT RE-FETCHES RATHER THAN CALCULATING
 * The obvious fix is to do the arithmetic here, and the theme's own free
 * shipping handler does exactly that. It is wrong for this shop. That handler
 * reads its limit as `limit * 100 * Shopify.currency.rate`, which assumes one
 * threshold held in the shop's currency and converted per market. Ours are
 * already stored in each market's own currency, so multiplying by the rate would
 * inflate every non-GBP market: €80 would be presented as €93.60 at a 1.17 rate.
 * The money formatting has the same problem.
 *
 * Liquid already knows the market, the threshold, the currency and the wording.
 * Rather than reimplement that here and have two versions of the truth, this
 * re-renders api-cart-items and lifts the finished bar out of the response.
 *
 * The checkout button's amount comes out of the same response, from the
 * subtotal row inside [data-api-cart-price] (rendered by cart-price.liquid,
 * hidden on screen, ISO code already stripped server-side). The alternative -
 * theme.formatMoney(total_price) here - uses moneyWithCurrencyFormat, which is
 * exactly the "£15.00 GBP" the client asked to remove, so the formatting stays
 * server-side. The button's Liquid gives it the right amount on page load;
 * this keeps it right afterwards.
 *
 * WHY A MUTATION OBSERVER (AND THE CART EVENTS AS WELL)
 * The theme dispatches five different cart events (theme:cart:add, :change,
 * :update, :refresh and the drawer's own) and not every path fires all of them.
 * The subtotal, though, changes exactly when the basket changes and never
 * otherwise, and the theme replaces that node itself. Watching it catches every
 * route in without needing to know which event a given control uses.
 */
(function () {
  'use strict';

  // The snippet renders wherever the bar appears, so this file can be requested
  // more than once on a page. Two observers would mean two fetches per change.
  if (window.__fsbarLive) return;
  window.__fsbarLive = true;

  var SECTION_URL = (window.theme && window.theme.routes && window.theme.routes.cart_url
    ? window.theme.routes.cart_url
    : '/cart') + '?section_id=api-cart-items';

  var BAR = '[data-free-shipping-bar]';
  // The delivery top-up suggestions, rendered by snippets/cart-topup.liquid.
  // Same contract as the bar: Liquid decides everything, this moves the finished
  // markup across. No product data, no thresholds and no arithmetic here.
  var TOPUP_SRC = '[data-api-cart-topup]';
  var TOPUP_HOLDER = '[data-cart-topup-holder]';
  // The gift-with-purchase nudge, rendered by snippets/gwp-notice.liquid.
  var GWP_SRC = '[data-api-cart-gwp]';
  var GWP_HOLDER = '[data-cart-gwp-holder]';
  var PRICE = '[data-cart-price-holder]';
  var AMOUNT = '[data-checkout-price]';
  var FRESH_TOTAL = '[data-api-cart-price] [data-cart-total]';

  var pending = null;
  var inFlight = false;
  var dirty = false;

  function refresh() {
    // A change landing while a fetch is airborne must not be dropped: the
    // response being rendered predates it. Mark dirty and go again after.
    if (inFlight) { dirty = true; return; }
    // The bar can be absent (markets with no threshold entry) while the button
    // amount still needs updating, and vice versa on pages with a bar but no
    // checkout button in view. Either one justifies the fetch; neither, nothing.
    if (!document.querySelector(BAR) && !document.querySelector(AMOUNT)) return;

    inFlight = true;
    fetch(SECTION_URL)
      .then(function (r) {
        // A 5xx/429 comes back as an error page with no bar in it, which the
        // code below would read as "this market has no threshold" and remove
        // the bar for the rest of the session. Non-OK goes to the catch, which
        // correctly leaves the last good state standing. (Red team, reproduced.)
        if (!r.ok) throw new Error('fsbar ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var holder = document.createElement('div');
        holder.innerHTML = html;
        var fresh = holder.querySelector(BAR);

        // The checkout guard, kept honest across AJAX changes: the api payload
        // carries a fresh server-side verdict; the fieldset and the warning
        // follow it in both directions (page-load state went stale both ways).
        // Two independent verdicts now: missing options, and more than one
        // Sample Service. The fieldset is disabled if EITHER blocks; each
        // warning follows only its own flag, so the customer is told the thing
        // they can actually act on.
        var verdict = holder.querySelector('[data-api-options-guard]');
        var sampleVerdict = holder.querySelector('[data-api-sample-guard]');
        // Third: made-to-order pieces without the agreement to the wait.
        var mtoVerdict = holder.querySelector('[data-api-mto-guard]');
        if (verdict || sampleVerdict || mtoVerdict) {
          var blocked = verdict ? verdict.getAttribute('data-blocked') === 'true' : false;
          var sampleBlocked = sampleVerdict ? sampleVerdict.getAttribute('data-blocked') === 'true' : false;
          var mtoBlocked = mtoVerdict ? mtoVerdict.getAttribute('data-blocked') === 'true' : false;
          var anyBlocked = blocked || sampleBlocked || mtoBlocked;
          document.querySelectorAll('[data-cart-checkout-button]').forEach(function (fs) {
            fs.disabled = anyBlocked;
            // inert covers keyboard/AT reach into the wallet iframes, which
            // neither disabled nor pointer-events can (see cart-guard.css).
            fs.toggleAttribute('inert', anyBlocked);
          });
          document.querySelectorAll('.cart__options-warning').forEach(function (w) {
            if (w.classList.contains('cart__sample-warning')) {
              w.hidden = !sampleBlocked;
            } else if (w.classList.contains('cart__mto-warning')) {
              w.hidden = !mtoBlocked;
            } else {
              w.hidden = !blocked;
            }
          });
        }

        document.querySelectorAll(BAR).forEach(function (bar) {
          if (!fresh) {
            // No bar in the response means this market has no threshold, or the
            // basket currency stopped matching it. Both are "show nothing".
            bar.remove();
            return;
          }
          // Swap the contents, not the element: the wrapper carries the
          // placement class that positions this particular copy, and the drawer
          // and cart page use different ones.
          bar.innerHTML = fresh.innerHTML;
          bar.setAttribute('data-threshold', fresh.getAttribute('data-threshold') || '');
          bar.classList.toggle('fsbar--qualified', fresh.classList.contains('fsbar--qualified'));
        });

        // The top-up suggestions. The holder is kept and its contents
        // replaced, because the holder is what page load put in the DOM and
        // nothing here ever inserts one. An empty response empties it, which is
        // the correct display for a qualified basket - the snippet renders
        // nothing once the threshold is met.
        //
        // The placement modifier is restamped from the holder rather than
        // trusted from the response: the api section renders one placement for
        // everybody, and the cart page's copy needs the other one. Getting this
        // wrong is invisible in the markup and shows up only as wrong spacing.
        var freshTopup = holder.querySelector(TOPUP_SRC);
        document.querySelectorAll(TOPUP_HOLDER).forEach(function (h) {
          h.innerHTML = freshTopup ? freshTopup.innerHTML : '';
          var place = h.getAttribute('data-cart-topup-holder');
          var card = place && h.querySelector('.topup');
          if (card) {
            card.classList.remove('topup--drawer', 'topup--cart');
            card.classList.add('topup--' + place);
          }
        });

        // The gift-with-purchase nudge. Same keep-the-holder rule as the
        // top-up; spacing is keyed off the holder in CSS, so nothing to restamp.
        var freshGwp = holder.querySelector(GWP_SRC);
        document.querySelectorAll(GWP_HOLDER).forEach(function (h) {
          h.innerHTML = freshGwp ? freshGwp.innerHTML : '';
        });

        // The subtotal inside the checkout button, lifted from the hidden
        // server-rendered subtotal row so the formatting (and the stripped ISO
        // code) stays Liquid's. Text only - the span carries no markup.
        var freshTotal = holder.querySelector(FRESH_TOTAL);
        if (freshTotal) {
          var amount = freshTotal.textContent.trim();
          document.querySelectorAll(AMOUNT).forEach(function (el) {
            if (el.textContent !== amount) el.textContent = amount;
          });
        }
      })
      .catch(function () {
        // Leave the last good state on screen. A stale bar is a small wrong; a
        // blank or broken one next to a checkout button is a larger one.
      })
      .finally(function () {
        inFlight = false;
        if (dirty) { dirty = false; schedule(); }
      });
  }

  function schedule() {
    clearTimeout(pending);
    // Long enough to coalesce the burst of DOM writes the theme makes when it
    // patches the basket, short enough that the bar moves while the eye is still
    // on it.
    pending = setTimeout(refresh, 150);
  }

  function watch() {
    var holders = document.querySelectorAll(PRICE);
    if (!holders.length) return false;
    var observer = new MutationObserver(schedule);
    holders.forEach(function (holder) {
      observer.observe(holder, { childList: true, subtree: true, characterData: true });
    });
    return true;
  }

  function start() {
    if (watch()) return;
    // The drawer is in a section group and can be parsed after this script runs.
    // Wait for the price holder to exist rather than giving up.
    var wait = new MutationObserver(function () {
      if (watch()) wait.disconnect();
    });
    wait.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // Belt and braces (28 Sep 2026): the subtotal observer misses any change
  // that leaves the subtotal text the same (swap a £25 soap for the £25 Sample
  // Service) and was seen to lag on some routes. The theme's own basket events
  // are added as extra triggers; schedule() debounces, so a change that fires
  // several of them still costs one fetch.
  ['theme:cart:change', 'theme:cart:refresh', 'theme:product:added', 'theme:cart-drawer:open'].forEach(function (name) {
    document.addEventListener(name, schedule);
  });

  // Theme editor re-renders replace the section's DOM, leaving the observer
  // pointed at detached nodes. Re-attach on the editor's own event; watch()
  // simply adds observers for whatever price holders now exist.
  document.addEventListener('shopify:section:load', function () { start(); });
})();
